import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { STABLE_QUERY_CONFIG } from "@config/constants";
import { useDataApi } from "@core/apis";

import { getSubstationByOrganizationQueryOptions } from "../queries";
import { getOrganizationListQueryOptions } from "../queries/organization.queries";
import type { InfoBlock } from "../types";

export const usePropAiSubstationByOrganization = (
  orgId: string,
  { enabled }: { enabled?: boolean }
) => {
  const dataApi = useDataApi();
  return useQuery({
    ...getSubstationByOrganizationQueryOptions(dataApi, orgId),
    ...STABLE_QUERY_CONFIG,
    enabled: !!orgId && !!dataApi && !!enabled,
  });
};

/**
 * Shared primitive for fetching substation info blocks through ISharedDataApi.
 * Consumes the unified adapter boundary — no PropAI/Platform branching at the
 * hook layer.
 */
export const useInfoBlocks = (
  substationUid: string | undefined,
  blockNames: string[],
  options?: { networkUid?: string; enabled?: boolean }
) => {
  const dataApi = useDataApi();
  const networkUid = options?.networkUid;
  const enabled = options?.enabled ?? true;

  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["info-blocks", substationUid, blockNames, networkUid],
    queryFn: () => dataApi!.getInfoBlocks(substationUid!, blockNames, networkUid),
    enabled: enabled && !!dataApi && !!substationUid && blockNames.length > 0,
    ...STABLE_QUERY_CONFIG,
  });
};

const DEFAULT_WEATHER_COORDINATES = { lat: 59.911491, lon: 10.757933 };

const parseCoordinatePair = (value: unknown): { lat: number; lon: number } | null => {
  if (!value || typeof value !== "string") return null;
  const [lat, lon] = value.split(",").map(Number);
  if (isNaN(lat) || isNaN(lon)) return null;
  return { lat, lon };
};

const selectWeatherCoordinates = (
  data: { collection?: InfoBlock[] } | undefined
): { lat: number; lon: number } => {
  const locationBlock = data?.collection?.find((block) => block.block_name === "location");
  if (!locationBlock?.block_data) return DEFAULT_WEATHER_COORDINATES;

  return (
    parseCoordinatePair(locationBlock.block_data.weather_coordinates) ??
    parseCoordinatePair(locationBlock.block_data.coordinates) ??
    DEFAULT_WEATHER_COORDINATES
  );
};

/**
 * Fetch weather coordinates for a substation from its `location` info block.
 * Falls back to Oslo when the block is missing or malformed.
 */
export const useWeatherCoordinates = (substationUid: string, networkUid?: string) => {
  const { data, isLoading } = useInfoBlocks(substationUid, ["location"], { networkUid });
  const coordinates = useMemo(() => selectWeatherCoordinates(data), [data]);
  return { data: coordinates, isLoading };
};

/**
 * Fetch the street address of a substation from its `install_address` info block.
 *
 * @param substationUid - The substation UID to fetch address for
 * @param fallbackAddress - Optional fallback address using route context
 * @param networkUid - Optional network UID (required by Platform API)
 * @returns Object with address string and isLoading boolean
 */
export const useSubstationAddress = (
  substationUid: string | undefined,
  fallbackAddress?: string,
  networkUid?: string
) => {
  const { data: installAddressData, isLoading } = useInfoBlocks(
    substationUid,
    ["install_address"],
    { networkUid }
  );

  const address = useMemo(() => {
    const block = installAddressData?.collection?.find((b) => b.block_name === "install_address");
    const blockData =
      (block?.block as { street?: string } | undefined) ??
      (block?.block_data as { street?: string } | undefined);
    return blockData?.street ?? fallbackAddress ?? "";
  }, [installAddressData, fallbackAddress]);

  return { address, isLoading };
};

/**
 * Hook to fetch and extract the organization name.
 *
 * @param organizationId - The organization UID to fetch name for
 * @param fallbackName - Optional fallback name using route context
 * @returns Object with organizationName string and isLoading boolean
 */
export const useOrganizationName = (organizationId: string | undefined, fallbackName?: string) => {
  const dataApi = useDataApi();

  const { data: organizationsData, isLoading } = useQuery({
    ...getOrganizationListQueryOptions(dataApi),
    ...STABLE_QUERY_CONFIG,
    enabled: !!organizationId && !!dataApi,
  });

  const organizationName = useMemo(() => {
    if (organizationsData && organizationId) {
      const org = organizationsData.find((o) => o.uid === organizationId);
      if (org?.name) {
        return org.name;
      }
    }
    // Fallback
    return fallbackName ?? "";
  }, [organizationsData, organizationId, fallbackName]);

  return { organizationName, isLoading };
};
